import React from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';

const styles = {
  page: {
    background: '#f7f7f7',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif',
    color: '#1f2937',
  },
  headerWrap: {
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '24px 20px 60px',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '20px',
  },
  backButton: {
    border: 'none',
    background: '#111827',
    color: '#fff',
    padding: '10px 18px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  card: {
    background: '#ffffff',
    borderRadius: '18px',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
    padding: '32px 28px',
  },
  title: {
    margin: '0 0 12px',
    fontSize: '34px',
    fontWeight: '700',
    color: '#111827',
  },
  intro: {
    margin: '0 0 18px',
    fontSize: '17px',
    lineHeight: 1.8,
    color: '#374151',
  },
  meta: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f172a',
    margin: '0 0 26px',
  },
  section: {
    marginTop: '28px',
  },
  sectionTitle: {
    fontSize: '28px',
    margin: '0 0 14px',
    color: '#111827',
  },
  subsectionTitle: {
    fontSize: '20px',
    margin: '18px 0 10px',
    color: '#1f2937',
  },
  paragraph: {
    margin: '0 0 12px',
    fontSize: '16px',
    lineHeight: 1.8,
    color: '#374151',
  },
  list: {
    margin: '0 0 14px 20px',
    padding: 0,
    color: '#374151',
    lineHeight: 1.8,
    fontSize: '16px',
  },
  strongText: {
    fontWeight: 700,
    color: '#111827',
  },
  contactBox: {
    marginTop: '18px',
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '18px 20px',
    lineHeight: 1.8,
  },
  footerNote: {
    marginTop: '28px',
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
    textAlign: 'center',
  },
};

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.headerWrap}>
        <Header />
      </div>

      <div style={styles.container}>
        <div style={styles.topBar}>
          <button style={styles.backButton} onClick={() => navigate('/')}>
            ← Back
          </button>
        </div>

        <div style={styles.card}>
          <h1 style={styles.title}>Privacy Policy</h1>

          <p style={styles.intro}>
            Your privacy matters. At MyHelpz, we are committed to protecting your personal
            information and being transparent about how we collect, use, store, and safeguard
            your data.
          </p>

          <p style={styles.meta}>Effective Date: 13 September 2026</p>

          <p style={styles.paragraph}>
            Welcome to MyHelpz (“MyHelpz”, “Company”, “we”, “our”, or “us”). MyHelpz is
            India’s On-Demand Human Assistance Platform, connecting users with trained,
            verified, and trusted Assistants for a wide range of everyday support services.
          </p>

          <p style={styles.paragraph}>
            This Privacy Policy explains how we collect, use, disclose, and protect your
            information when you use the MyHelpz mobile application, website, and related
            services.
          </p>

          <p style={styles.paragraph}>
            By accessing or using MyHelpz, you acknowledge that you have read, understood,
            and agreed to the practices described in this Privacy Policy.
          </p>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>1. Information We Collect</h2>

            <h3 style={styles.subsectionTitle}>Personal Information</h3>
            <p style={styles.paragraph}>
              We may collect the following information when you register or use MyHelpz
              services:
            </p>
            <ul style={styles.list}>
              <li>Full Name</li>
              <li>Email Address</li>
              <li>Mobile Phone Number</li>
              <li>Profile Photograph (Optional)</li>
              <li>Residential Address (Optional)</li>
              <li>Emergency Contact Details (Optional)</li>
              <li>Location Information</li>
            </ul>

            <p style={styles.paragraph}>
              With your permission, MyHelpz may collect precise or approximate location
              information to:
            </p>
            <ul style={styles.list}>
              <li>Connect users with nearby Assistants</li>
              <li>Enable navigation and route tracking</li>
              <li>Facilitate service delivery</li>
              <li>Improve service quality and response times</li>
              <li>Verify service completion</li>
              <li>Enhance user safety and security</li>
            </ul>

            <h3 style={styles.subsectionTitle}>Service and Booking Information</h3>
            <p style={styles.paragraph}>
              We may collect information related to services booked through MyHelpz, including:
            </p>
            <ul style={styles.list}>
              <li>Booking details</li>
              <li>Service category</li>
              <li>Assistant assigned</li>
              <li>Service duration</li>
              <li>Service location</li>
              <li>Ratings and reviews</li>
              <li>Communication records related to bookings</li>
              <li>Customer support interactions</li>
            </ul>

            <h3 style={styles.subsectionTitle}>Payment Information</h3>
            <p style={styles.paragraph}>
              Payments are processed through secure third-party payment providers such as
              Razorpay or other authorized payment gateways. MyHelpz does not store complete
              debit card, credit card, UPI PIN, net banking credentials, or sensitive banking
              information on its servers.
            </p>

            <h3 style={styles.subsectionTitle}>Device and Usage Information</h3>
            <p style={styles.paragraph}>
              We may automatically collect:
            </p>
            <ul style={styles.list}>
              <li>Device model and manufacturer</li>
              <li>Operating system and version</li>
              <li>IP address</li>
              <li>Mobile network information</li>
              <li>App version</li>
              <li>Device identifiers</li>
              <li>Browser information</li>
              <li>Crash logs and diagnostics</li>
              <li>Usage analytics and interaction data</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>2. How We Use Your Information</h2>
            <p style={styles.paragraph}>
              We use information collected through MyHelpz to:
            </p>
            <ul style={styles.list}>
              <li>Create and manage user accounts</li>
              <li>Provide human assistance services</li>
              <li>Match users with suitable Assistants</li>
              <li>Process and verify bookings</li>
              <li>Facilitate payments and refunds</li>
              <li>Send booking confirmations and updates</li>
              <li>Provide customer support</li>
              <li>Improve platform functionality and performance</li>
              <li>Monitor service quality</li>
              <li>Prevent fraud, abuse, and unauthorized activities</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Communicate service-related announcements and important updates</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>3. Third-Party Services</h2>
            <p style={styles.paragraph}>
              MyHelpz may use trusted third-party service providers that process information
              according to their respective privacy policies.
            </p>

            <h3 style={styles.subsectionTitle}>Google Maps Platform</h3>
            <p style={styles.paragraph}>
              We use Google Maps services for location services, navigation and routing,
              distance and travel-time calculations, and address verification.
            </p>
            <p style={styles.paragraph}><span style={styles.strongText}>Google Privacy Policy:</span> https://policies.google.com/privacy</p>

            <h3 style={styles.subsectionTitle}>Firebase</h3>
            <p style={styles.paragraph}>
              We use Firebase services for user authentication, analytics, push notifications,
              crash reporting, cloud database services, and file storage.
            </p>
            <p style={styles.paragraph}><span style={styles.strongText}>Firebase Privacy Information:</span> https://firebase.google.com/support/privacy</p>

            <h3 style={styles.subsectionTitle}>Payment Gateways</h3>
            <p style={styles.paragraph}>
              We may use payment service providers such as Razorpay and other authorized
              partners for secure payment processing.
            </p>
            <p style={styles.paragraph}><span style={styles.strongText}>Razorpay Privacy Policy:</span> https://razorpay.com/privacy</p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>4. Information Sharing</h2>
            <p style={styles.paragraph}>
              We do not sell, rent, or trade personal information to third parties. We may
              share limited information as described below:
            </p>

            <h3 style={styles.subsectionTitle}>With MyHelpz Assistants</h3>
            <p style={styles.paragraph}>To facilitate booked services, Assistants may receive:</p>
            <ul style={styles.list}>
              <li>Customer name</li>
              <li>Contact number</li>
              <li>Booking information</li>
              <li>Service location</li>
              <li>Service instructions</li>
            </ul>

            <h3 style={styles.subsectionTitle}>With Service Providers</h3>
            <p style={styles.paragraph}>
              We may share information with trusted vendors who assist in cloud hosting,
              payment processing, customer support, data analytics, communication services,
              and security and fraud prevention.
            </p>

            <h3 style={styles.subsectionTitle}>Legal Compliance</h3>
            <p style={styles.paragraph}>
              We may disclose information when required by applicable law, court order,
              government authority, legal process, or regulatory requirements.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>5. Data Security</h2>
            <p style={styles.paragraph}>
              We implement industry-standard security measures to protect user information,
              including secure HTTPS communication, data encryption during transmission,
              authentication and access controls, restricted internal access, and security
              monitoring and audits.
            </p>
            <p style={styles.paragraph}>
              While we strive to maintain the highest standards of security, no method of
              electronic storage or transmission over the Internet is completely secure.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>6. Data Retention</h2>
            <p style={styles.paragraph}>
              We retain information only for as long as necessary to provide our services,
              maintain account functionality, resolve disputes, enforce agreements, and meet
              legal, regulatory, and tax obligations.
            </p>
            <p style={styles.paragraph}>
              When information is no longer required, it is securely deleted, anonymized, or
              archived in accordance with applicable laws.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>7. Your Rights and Choices</h2>
            <p style={styles.paragraph}>
              Depending on applicable laws, you may have the right to access your personal
              information, update or correct inaccurate information, request deletion of your
              account, withdraw previously granted permissions, opt out of certain
              communications, and request information about how your data is used.
            </p>
            <p style={styles.paragraph}>
              You may manage certain permissions, including location access, through your
              device settings.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>8. Account Deletion</h2>
            <p style={styles.paragraph}>
              Users may request account deletion by using the Account Deletion feature within
              the MyHelpz application or by contacting our support team through the contact
              details provided below.
            </p>
            <p style={styles.paragraph}>
              Upon receiving a valid request, we will process the deletion within a reasonable
              timeframe, subject to legal, regulatory, and operational requirements.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>9. Children&apos;s Privacy</h2>
            <p style={styles.paragraph}>
              MyHelpz services are not intended for children under the age of 13 years. We do
              not knowingly collect personal information from children. If we become aware that
              a child has provided personal information, we will take appropriate steps to
              remove such information.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>10. Changes to this Privacy Policy</h2>
            <p style={styles.paragraph}>
              We may update this Privacy Policy from time to time. Updated versions will be
              published on the MyHelpz website and the MyHelpz mobile application. Your
              continued use of MyHelpz after any updates constitutes acceptance of the revised
              Privacy Policy.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>11. Contact Us</h2>
            <div style={styles.contactBox}>
              <p style={styles.paragraph}><span style={styles.strongText}>MyHelpz Support</span></p>
              <p style={styles.paragraph}>📧 Email: support@myhelpz.com</p>
              <p style={styles.paragraph}>🌐 Website: https://myhelpz.com</p>
            </div>
          </section>

          <p style={styles.footerNote}>MyHelpz - Your Home, Our Help.</p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
